import { useMemo, useState, useEffect } from "react";
import { fetchBoardCategories } from "../utils/api/fetchBoardCategories";
import { FormField, FormValues } from "../types/Board";
import { SubmitHandler } from "react-hook-form";
import { postBoard } from "../utils/api/postBoard";
import { fetchNGWords } from "../utils/api/fetchNGWords";
import { useHistory, useParams } from "react-router-dom";
import { putBoard } from "../utils/api/putBoard";
import { getSubdomain } from "../utils/getSubdomain";
import { useImageUploader } from "./useImageUploader";

export const LIB_STORAGE_BOARD_SUBMISSION_STATUS = 'lib_storage_board_submission_status';
const MAX_FILE_SIZE = 10 * 1024 * 1024 //10MB

type BoardFormData = {
  board_category_id: string | number;
  pictures?: FileList | string[] | any;
  [key: string]: any;
};

type ApiResponse = {
  isSuccess: boolean;
  message?: string;
  result?: {
    data?: {
      is_public: boolean;
      status: number;
      id: number;
    };
  };
};

const PUBLIC_STATUS = 0;
export const useGetBoardCategories = () => {
    const [boardCategories, setBoardCategories] = useState<any>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
  
    useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetchBoardCategories();
        const { result: categories = [] } = response ?? {};
        if (Array.isArray(categories) && categories.length > 0) {
          const mappingOptions = categories.map(
            (category: { id: number; title: string }) => {
              const { id, title } = category;
              return { id, value: id, title };
            }
          );
          setBoardCategories(mappingOptions);
        }
      } catch (err) {
        throw new Error(`Something failed`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
    }, []);
    
    return { boardCategories, isLoading }
}

export const useGetNGWords = () => {
    const [ngWords, setNGWords] = useState<any>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
    const getAllNGWords = async () => {
      try {
        const response = await fetchNGWords();
        const { result: ngWords = [] } = response ?? {};
        if (Array.isArray(ngWords) && ngWords.length > 0) {
          const mappingNGWords = ngWords.map(
            (ngWord: { id: number; word: string }) => {
              const { word } = ngWord;
              return word;
            }
          );
          setNGWords(mappingNGWords);
        }
      } catch (err) {
        throw new Error(`Something failed`);
      } finally {
        setIsLoading(false);
      }
    };
    getAllNGWords();
    }, []);
    
    return { ngWords, isLoading }
}

type ApiHandler = (body: any) => Promise<ApiResponse>;

export const useMutationBoardForm = (
  setSnackbarInfor: (info: { severity: 'success' | 'error'; message: string }) => void
) => {
  const [isPendingApproval, setIsPendingApproval] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const { category_id = 0, board_id = 0 } = useParams<{ category_id?: string , board_id?: string }>() ?? {};
  const history = useHistory();
  const subdomain = getSubdomain(window.location.hostname);
  const { uploadImage } = useImageUploader(subdomain);

  const handleApiResponse = (
    response: ApiResponse,
    bodyId?: number,
    bodyCategoryId?: number

  ) => {
    const { isSuccess, message = '', result } = response;

    if (!isSuccess) {
      throw new Error(message);
    } 

    const { is_public = false, status, id } = result?.data ?? {};
    setIsPendingApproval(!is_public);
    
    if (is_public && Number(status) === PUBLIC_STATUS) {
      localStorage.setItem(LIB_STORAGE_BOARD_SUBMISSION_STATUS, JSON.stringify({
        severity: 'success',
        message: '投稿が完了しました',
      }))
      const targetId = bodyId || id;
      const categoryId = bodyCategoryId || category_id
      history.push(
        `/message-board/categories/${categoryId}/${targetId}`
      );
    }
  };

  const executeBoardApiCall = async (
    apiHandler: ApiHandler,
    body: any
  ) => {
    const response = await apiHandler(body);
    const { id, board_category_id } = body ?? {}
    handleApiResponse(response, id, board_category_id);
  };

  const onSubmit: SubmitHandler<BoardFormData> = async (formValues) => {
    try {
      setIsFormLoading(true);
      let imageUrls: any = []
      if (Array.isArray(formValues.pictures) && formValues.pictures.length !== 0) {
        const currentPictures = Array.from(formValues.pictures);
        imageUrls = await Promise.all(
        currentPictures.map(uploadImage)
      );
      }

      const baseBody = {
        ...formValues,
        image_urls: imageUrls,
        board_category_id: Number(formValues.board_category_id),
      };

      delete baseBody.pictures;

      if (Boolean(board_id)) {
        await executeBoardApiCall(putBoard, {
          ...baseBody,
          id: board_id,
        });
      } else {
        await executeBoardApiCall(postBoard, baseBody);
      }
    } catch (error) {
      setSnackbarInfor({
        severity: 'error',
        message: '投稿に失敗しました',
      });
    } finally {
      setIsFormLoading(false);
    }
  };

  return {
    isFormLoading,
    onSubmit,
    isPendingApproval,
    setIsPendingApproval,
  };
};

export const countChars = (input: string) => {
  if (!input) return 0;
  const SINGLE_BYTE = /[\u0020-\u1fff\uff61-\uff9f]/g;
  const DOUBLE_BYTE = /[\u2000-\uff60\uffa0-\uffff]/g;

  const singleByteCount = (input.match(SINGLE_BYTE) || []).length;
  const doubleByteCount = (input.match(DOUBLE_BYTE) || []).length * 2;
  return singleByteCount + doubleByteCount;
};

const validateChars = (input: string, maxLength: number) => {
    if (!input?.trim()) {
        return null
    }
    if (countChars(input) > maxLength) {
        return `${maxLength / 2}字以内で入力してください`
    }
    return null
}

export const useFormConfig = (boardCategories: any) => {
    const formFields = useMemo(
    (): Record<keyof FormValues, FormField> => ({
      board_category_id: {
        title: "投稿するカテゴリ",
        type: "select",
        options: boardCategories,
        rules: { required: "投稿するカテゴリは必須です" },
      },
      avatar: {
        title: "表示名",
      },
      title: {
        title: "タイトル",
        type: "input",
          rules: {
              required: "タイトルは必須です",
              validate: {
                checkCountChar: (title: string) => {
                    return validateChars(title, 100)
                  }
              }
          },
      },
      content: {
        title: "投稿内容",
        type: "textarea",
          rules: {
              required: "投稿内容は必須です",
              validate: {
                checkCountChar: (title: string) => {
                    return validateChars(title, 2000)
                }
          }
          },
      },
      pictures: {
        title: "画像添付　※任意",
        type: "upload_file",
        rules: {
          validate: {
            moreThan10MB: (files: any) => {
              if (!files) {
                return undefined
              }
              let selectedFiledToArray = Array.from(files);
              if (selectedFiledToArray.length === 0) {
                return undefined
              }
              const hasMaxSizeFile = selectedFiledToArray?.some((file: any) => {
                if (typeof file === 'string') {
                  return false;
                }
                return file.size > MAX_FILE_SIZE;
              })
              if (hasMaxSizeFile) {
                return `ファイルサイズは10MB以下の画像を指定してください。`;
              } else {
                return undefined
              }
             }
          },
        }
      },
    }),
    [boardCategories]
  );
 return formFields
}